import { useEffect, useRef, useCallback } from 'react';
import { apiFetchUrl, resolveWebSocketUrl, getValidAccessToken } from './api';

interface UseNotificationPushOptions {
  enabled?: boolean;
  userId?: string;
  organizationId?: string;
  portalType?: string;
  onNotification?: () => void;
}

export function useNotificationPush({
  enabled = true,
  userId,
  organizationId,
  portalType,
  onNotification,
}: UseNotificationPushOptions) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onNotificationRef = useRef(onNotification);
  onNotificationRef.current = onNotification;

  const connect = useCallback(async () => {
    if (!enabled || !userId || !organizationId || !portalType) return;
    if (typeof WebSocket === 'undefined') return;

    const token = await getValidAccessToken();
    if (!token) return;

    const query = new URLSearchParams({ userId, organizationId, portalType });
    const url = resolveWebSocketUrl(apiFetchUrl(`/ws/notifications?${query.toString()}`));

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data as string);
          if (msg.type === 'notification') {
            onNotificationRef.current?.();
          }
        } catch {
          // ignore malformed payloads
        }
      };

      ws.onclose = (ev) => {
        wsRef.current = null;
        if (enabled && ev.code !== 1008) {
          reconnectTimerRef.current = setTimeout(connect, 5000);
        }
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch {
      // WebSocket unavailable — polling remains the fallback
    }
  }, [enabled, userId, organizationId, portalType]);

  useEffect(() => {
    if (!enabled) return;
    connect();
    return () => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [enabled, connect]);
}
